import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useOptionalLifeOS } from './LifeOSProvider';
import {
  lifeOSClassNames,
  lifeOSDateTime,
  lifeOSItemExcerpt,
  lifeOSItemTitle,
  lifeOSStatusLabel,
  lifeOSTypeLabel,
} from './presentation';
import './life-os.css';

const EMPTY_INBOX_STATE = Object.freeze({});

function inboxItems(data, state) {
  if (Array.isArray(state?.items)) return state.items;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.inbox)) return data.inbox;
  return [];
}

function inboxSuggestion(item) {
  const suggestion = item?.classification_suggestion || item?.suggestion || item?.suggested_type || item?.classification;
  if (typeof suggestion === 'string') return suggestion.trim();
  if (suggestion && typeof suggestion === 'object') {
    return String(suggestion.type || suggestion.label || suggestion.kind || '').trim();
  }
  return '';
}

function normalizedProjects(projects) {
  return (Array.isArray(projects) ? projects : []).map((project) => ({
    id: project?.id || project?.project_id,
    name: project?.name || project?.title,
  })).filter(project => project.id && project.name);
}

function defaultConfirmDiscard(item) {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') return true;
  const title = lifeOSItemTitle(item);
  return window.confirm(title ? `Descartar “${title}”?` : 'Descartar este item?');
}

function InboxLoading() {
  return (
    <div className="life-os-loading-list" role="status" aria-label="Carregando Inbox">
      {[0, 1, 2].map(index => <div className="life-os-skeleton life-os-skeleton--item" key={index} aria-hidden="true" />)}
      <span className="life-os-sr-only">Carregando Inbox…</span>
    </div>
  );
}

/**
 * Reviewable Inbox surface. Destructive or promotional actions only happen
 * after an explicit click and an API-confirmed response.
 */
export function InboxPanel({
  data,
  state,
  status,
  autoLoad = true,
  className,
  title = 'Inbox',
  maxVisibleItems = 24,
  showCapture = true,
  capturePlaceholder = 'Anote algo para revisar depois…',
  projects,
  onChooseProject,
  onCapture,
  onReview,
  onRefresh,
  onLoadMore,
  onSelectItem,
  onActionComplete,
  confirmDiscard = defaultConfirmDiscard,
  emptyMessage = 'Sua Inbox está vazia.',
}) {
  const panelId = useId();
  const lifeOS = useOptionalLifeOS();
  const providerState = lifeOS?.inbox;
  const effectiveState = state || providerState || EMPTY_INBOX_STATE;
  const response = data !== undefined ? data : effectiveState.data;
  const items = useMemo(() => inboxItems(response, data === undefined ? effectiveState : null), [data, effectiveState, response]);
  const loading = data !== undefined ? false : Boolean(effectiveState.loading);
  const error = data !== undefined ? '' : effectiveState.error;
  const refresh = onRefresh || (() => lifeOS?.loadInbox({ status }));
  const canLoadMore = Boolean(effectiveState.hasMore || response?.next_cursor || response?.nextCursor);
  const projectOptions = useMemo(() => normalizedProjects(projects), [projects]);
  const reviewItem = onReview || lifeOS?.reviewInbox;
  const captureItem = onCapture || lifeOS?.captureInbox;
  const busyAction = lifeOS?.inboxAction || { loading: false, itemId: null, action: null, error: '' };
  const [captureText, setCaptureText] = useState('');
  const [captureError, setCaptureError] = useState('');
  const [localBusy, setLocalBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [moveItemId, setMoveItemId] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const attemptedAutoLoadRef = useRef('');

  useEffect(() => {
    if (!autoLoad || data !== undefined || state?.data || !lifeOS || loading) return;
    const key = status || 'default';
    if (attemptedAutoLoadRef.current !== key) {
      attemptedAutoLoadRef.current = key;
      void lifeOS.loadInbox({ status });
    }
  }, [autoLoad, data, effectiveState.data, lifeOS, loading, state?.data, status]);

  const visibleItems = items.slice(0, Math.max(1, Number(maxVisibleItems) || 24));
  const hasInlineProjects = projectOptions.length > 0;
  const isCaptureBusy = localBusy || (busyAction.loading && busyAction.action === 'capture');

  async function handleCapture(event) {
    event.preventDefault();
    const content = captureText.trim();
    if (!content || !captureItem || isCaptureBusy) return;
    setCaptureError('');
    setFeedback('');
    setLocalBusy(true);
    try {
      const result = await captureItem({ content, source: 'manual', source_type: 'text' });
      setCaptureText('');
      setFeedback(result?.duplicate ? 'Esta captura já estava na Inbox.' : 'Captura adicionada à Inbox.');
      onActionComplete?.({ action: 'capture', result });
    } catch (cause) {
      setCaptureError(cause?.response?.data?.error || cause?.response?.data?.detail || 'Não foi possível adicionar esta captura.');
    } finally {
      setLocalBusy(false);
    }
  }

  async function runReview(item, action, payload = {}) {
    if (!reviewItem || (busyAction.loading && busyAction.itemId === item?.id) || localBusy) return;
    if (action === 'discard' && !(await confirmDiscard?.(item))) return;
    setFeedback('');
    setLocalBusy(true);
    try {
      const result = await reviewItem(item?.id, action, payload);
      const promoted = result?.promoted?.type ? ` Item criado: ${lifeOSTypeLabel(result.promoted.type)}.` : '';
      setFeedback(`Atualização confirmada.${promoted}`);
      onActionComplete?.({ item, action, result });
      setMoveItemId(null);
      setSelectedProject('');
    } catch (cause) {
      setFeedback(cause?.response?.data?.error || cause?.response?.data?.detail || 'Não foi possível atualizar este item.');
    } finally {
      setLocalBusy(false);
    }
  }

  async function startMove(item) {
    if (onChooseProject) {
      try {
        const choice = await onChooseProject(item);
        const projectId = typeof choice === 'string' ? choice : (choice?.id || choice?.project_id);
        if (projectId) await runReview(item, 'move_to_project', { project_id: projectId });
      } catch (cause) {
        setFeedback(cause?.message || 'Não foi possível escolher um projeto.');
      }
      return;
    }
    setMoveItemId(item?.id);
    setSelectedProject('');
  }

  function loadMore() {
    if (onLoadMore) {
      void onLoadMore();
      return;
    }
    void lifeOS?.loadInbox({ ...(effectiveState.params || {}), status, append: true });
  }

  return (
    <section className={lifeOSClassNames('life-os-panel', 'life-os-inbox', className)} aria-labelledby={panelId}>
      <header className="life-os-panel__header">
        <div>
          <p className="life-os-eyebrow">ATHENA INBOX</p>
          <h2 id={panelId}>{title}</h2>
          <p className="life-os-panel__subheading">{items.length} {items.length === 1 ? 'item para revisar' : 'itens para revisar'}</p>
        </div>
        <button
          type="button"
          className="life-os-icon-button"
          onClick={() => { void refresh?.(); }}
          disabled={loading}
          aria-label="Atualizar Inbox"
          title="Atualizar"
        >
          ↻
        </button>
      </header>

      {showCapture && (
        <form className="life-os-capture" onSubmit={handleCapture}>
          <label htmlFor={`${panelId}-capture`} className="life-os-sr-only">Adicionar captura à Inbox</label>
          <textarea
            id={`${panelId}-capture`}
            value={captureText}
            onChange={event => setCaptureText(event.target.value)}
            placeholder={capturePlaceholder}
            maxLength={8000}
            rows={2}
            disabled={isCaptureBusy || !captureItem}
          />
          <div className="life-os-capture__footer">
            <span aria-live="polite">{captureError || (busyAction.action === 'capture' ? busyAction.error : '')}</span>
            <button type="submit" className="life-os-button" disabled={!captureText.trim() || isCaptureBusy || !captureItem}>
              {isCaptureBusy ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        </form>
      )}

      {(error || feedback) && (
        <div className={lifeOSClassNames('life-os-notice', error && 'life-os-notice--error')} role={error ? 'alert' : 'status'}>
          <span>{error || feedback}</span>
          {error && <button type="button" className="life-os-link-button" onClick={() => { void refresh?.(); }}>Tentar novamente</button>}
        </div>
      )}

      {loading && items.length === 0 ? <InboxLoading /> : (
        <>
          {visibleItems.length > 0 && (
            <ul className="life-os-inbox-list" aria-label="Itens da Inbox">
              {visibleItems.map((item, index) => {
                const titleText = lifeOSItemTitle(item) || lifeOSTypeLabel(item?.type) || 'Captura sem título';
                const excerpt = lifeOSItemExcerpt(item);
                const suggestion = inboxSuggestion(item);
                const isBusy = localBusy || (busyAction.loading && busyAction.itemId === item?.id);
                const isMoving = moveItemId === item?.id;
                return (
                  <li className="life-os-inbox-card" key={item?.id || `${titleText}-${index}`}>
                    <div className="life-os-inbox-card__main">
                      <div className="life-os-inbox-card__topline">
                        {lifeOSTypeLabel(item?.type || item?.kind) && <span className="life-os-kind">{lifeOSTypeLabel(item?.type || item?.kind)}</span>}
                        {item?.created_at && <time>{lifeOSDateTime(item.created_at)}</time>}
                      </div>
                      {onSelectItem ? (
                        <button type="button" className="life-os-inbox-card__title-button" onClick={() => onSelectItem(item)} aria-label={`Abrir ${titleText}`}>
                          {titleText}
                        </button>
                      ) : <h3>{titleText}</h3>}
                      {excerpt && <p>{excerpt}</p>}
                      <div className="life-os-inbox-card__facts">
                        {suggestion && <span>Sugestão: {lifeOSTypeLabel(suggestion)}</span>}
                        {lifeOSStatusLabel(item?.status) && <span>{lifeOSStatusLabel(item.status)}</span>}
                      </div>
                    </div>

                    {reviewItem && (
                      <div className="life-os-inbox-card__actions" aria-label={`Ações para ${titleText}`}>
                        <button type="button" className="life-os-text-action" disabled={isBusy} onClick={() => { void runReview(item, 'promote_task'); }}>
                          Transformar em tarefa
                        </button>
                        <button type="button" className="life-os-text-action" disabled={isBusy} onClick={() => { void runReview(item, 'save_to_vault'); }}>
                          Salvar no Vault
                        </button>
                        {(onChooseProject || hasInlineProjects) && (
                          <button type="button" className="life-os-text-action" disabled={isBusy} onClick={() => { void startMove(item); }}>
                            Mover para projeto
                          </button>
                        )}
                        <button type="button" className="life-os-text-action" disabled={isBusy} onClick={() => { void runReview(item, 'archive'); }}>
                          Arquivar
                        </button>
                        <button type="button" className="life-os-text-action life-os-text-action--danger" disabled={isBusy} onClick={() => { void runReview(item, 'discard'); }}>
                          Descartar
                        </button>
                      </div>
                    )}

                    {isMoving && hasInlineProjects && (
                      <div className="life-os-project-picker">
                        <label htmlFor={`${panelId}-project-${item.id}`}>Escolher projeto</label>
                        <select id={`${panelId}-project-${item.id}`} value={selectedProject} onChange={event => setSelectedProject(event.target.value)} disabled={isBusy}>
                          <option value="">Selecione um projeto</option>
                          {projectOptions.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                        </select>
                        <div>
                          <button type="button" className="life-os-button life-os-button--quiet" onClick={() => setMoveItemId(null)} disabled={isBusy}>Cancelar</button>
                          <button type="button" className="life-os-button" onClick={() => { void runReview(item, 'move_to_project', { project_id: selectedProject }); }} disabled={!selectedProject || isBusy}>Mover</button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && !error && items.length === 0 && <div className="life-os-empty" role="status"><p>{emptyMessage}</p></div>}
          {canLoadMore && (
            <button type="button" className="life-os-button life-os-button--quiet life-os-show-more" onClick={loadMore} disabled={loading}>
              {loading ? 'Carregando…' : 'Carregar mais'}
            </button>
          )}
        </>
      )}
    </section>
  );
}

export default InboxPanel;
